import React, { useState, useEffect } from 'react';
import { Header } from '../components/Header';
import { PageLoader } from '../components/LoadingSpinner';
import { coinsAPI } from '../lib/api';
import { formatNumber, formatDateTime } from '../lib/formatters';
import { Coins, TrendingUp, TrendingDown, Gift, ShoppingCart, DollarSign, AlertTriangle, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

export default function CoinWalletPage() {
  const [balance, setBalance] = useState(0);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [balanceRes, transactionsRes] = await Promise.all([
        coinsAPI.getBalance(),
        coinsAPI.getTransactions(),
      ]);
      setBalance(balanceRes.data.balance);
      setTransactions(transactionsRes.data);
    } catch (error) {
      console.error('Failed to fetch coin data:', error);
      setError('Unable to load wallet data. Please try again.');
      toast.error('Failed to load wallet data');
    } finally {
      setLoading(false);
    }
  };

  const getTransactionIcon = (type) => {
    const icons = {
      dividend: <Gift className="w-5 h-5 text-[hsl(var(--gain))]" />,
      purchase: <ShoppingCart className="w-5 h-5 text-[hsl(var(--loss))]" />,
      sale: <TrendingUp className="w-5 h-5 text-[hsl(var(--gain))]" />,
      transfer: <TrendingDown className="w-5 h-5 text-[hsl(var(--text-secondary))]" />,
      fee: <DollarSign className="w-5 h-5 text-[hsl(var(--text-tertiary))]" />,
      admin_grant: <Gift className="w-5 h-5 text-[hsl(var(--brand-primary))]" />,
    };
    return icons[type] || <Coins className="w-5 h-5 text-[hsl(var(--text-secondary))]" />;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[hsl(var(--background))]">
        <Header />
        <PageLoader text="Loading wallet..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[hsl(var(--background))]">
        <Header />
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-8">
          <div className="bg-red-50 border border-red-200 rounded-xl p-8 text-center">
            <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <p className="text-red-700 font-medium mb-2">Unable to load wallet</p>
            <p className="text-red-600 text-sm mb-4">{error}</p>
            <button
              onClick={fetchData}
              className="inline-flex items-center gap-2 px-6 py-2 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]">
      <Header />

      <main className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
        {/* Page Title */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-[hsl(var(--text-primary))] mb-2">
            Coin Wallet
          </h1>
          <p className="text-[hsl(var(--text-secondary))]">
            Manage your HUB coins for share purchases and trading
          </p>
        </div>

        {/* Balance Card */}
        <div
          data-testid="coin-balance-card"
          className="bg-gradient-to-br from-[hsl(var(--brand-primary))] to-[hsl(var(--brand-primary-dark))] rounded-xl p-8 mb-8 shadow-lg"
        >
          <div className="flex items-center gap-3 mb-2">
            <Coins className="w-8 h-8 text-white" />
            <span className="text-white text-lg font-medium">Available Balance</span>
          </div>
          <div className="text-5xl md:text-6xl font-bold text-white font-mono">
            {formatNumber(balance)}
          </div>
          <div className="text-white/80 text-sm mt-2">HUB Coins</div>
        </div>

        {/* Transaction History */}
        <div>
          <h2 className="text-2xl font-semibold text-[hsl(var(--text-primary))] mb-4">
            Transaction History
          </h2>

          {transactions.length === 0 ? (
            <div className="bg-white border border-[hsl(var(--border))] rounded-xl p-12 text-center">
              <Coins className="w-12 h-12 text-[hsl(var(--text-tertiary))] mx-auto mb-4" />
              <p className="text-[hsl(var(--text-secondary))]">No transactions yet</p>
            </div>
          ) : (
            <div className="bg-white border border-[hsl(var(--border))] rounded-xl overflow-hidden">
              <div data-testid="transaction-list" className="divide-y divide-[hsl(var(--border))]">
                {transactions.map((txn) => (
                  <div
                    key={txn.transaction_id}
                    data-testid={`transaction-${txn.transaction_id}`}
                    className="p-6 hover:bg-[hsl(var(--background-secondary))] transition-colors duration-150"
                  >
                    <div className="flex items-start justify-between gap-4">
                      {/* Left: Icon + Details */}
                      <div className="flex items-start gap-4 flex-1">
                        <div className="p-2 bg-[hsl(var(--background-secondary))] rounded-lg">
                          {getTransactionIcon(txn.transaction_type)}
                        </div>
                        <div className="flex-1">
                          <div className="font-semibold text-[hsl(var(--text-primary))] mb-1">
                            {txn.description}
                          </div>
                          <div className="text-sm text-[hsl(var(--text-secondary))]">
                            {formatDateTime(txn.timestamp)}
                          </div>
                          <div className="text-xs text-[hsl(var(--text-tertiary))] mt-1 font-mono">
                            {txn.transaction_type.replace('_', ' ').toUpperCase()}
                          </div>
                        </div>
                      </div>

                      {/* Right: Amount */}
                      <div className="text-right">
                        <div
                          className={`text-xl font-bold font-mono ${
                            txn.amount >= 0
                              ? 'text-[hsl(var(--gain))]'
                              : 'text-[hsl(var(--loss))]'
                          }`}
                        >
                          {txn.amount >= 0 ? '+' : ''}
                          {formatNumber(txn.amount)}
                        </div>
                        <div className="text-sm text-[hsl(var(--text-tertiary))] mt-1">
                          Balance: {formatNumber(txn.balance_after)}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
